/**
 * Фискальные команды кассового агента (Kalmykov-Cash-Agent, docs/CONTRACT.md): чек, смена, наличные, состояние кассы,
 * журнал операций, итоги смены, восстановление после обрыва, служебная печать.
 *
 * Работает поверх общего `LocalAgentClient` (единственное место клиента, где вообще ходят к агенту), сам HTTP не вызывает.
 * Правила, на которых держится безопасность денег:
 * - **ключ операции задаёт окно** (`newOperationId`) ДО отправки и хранит вместе с корзиной; повтор того же запроса с тем же
 *   ключом агент не пробивает второй раз, а отдаёт прежний итог;
 * - **обрыв связи при команде — не отказ.** Агент мог уже пробить чек: `unreachable` на POST означает «не знаем», а не «не вышло».
 *   Узнать исход — `operation(id)`; `null` (404) значит, что до журнала агента команда не дошла и пробивать можно с тем же ключом;
 * - 401 на команде значит «не выполнено»: токен обновляется и команда уходит ровно ещё раз, тем же ключом;
 * - русские тексты агента (`error.text`, `error.action`, `problems[].text`) не переписываются (ADR-8).
 */

import {
  LocalAgentRequestError,
  type LocalAgentClient,
} from './localAgent'

/** Чек пробит / не пробит / исход неизвестен (CONTRACT.md). */
export type FiscalStatus = 'done' | 'failed' | 'in-doubt'

export interface FiscalError {
  code: string
  text: string
  /** Что делать кассиру: совет агента по коду ошибки кассы. */
  action: string | null
}

export interface FiscalDocument {
  shiftNumber: number | null
  receiptNumber: number | null
  fiscalDocumentNumber: number | null
  fiscalSign: string | null
  fnNumber: string | null
  dateTime: string | null
  total: number | null
  registrationNumber: string | null
  /** `false` — документ пробит, но не напечатан (допечатать); `null` — агент не сказал. */
  printed: boolean | null
}

/** Исход оплаты картой в этой операции (`outcome` — слова агента как есть: approved, denied, unknown…). */
export interface FiscalCard {
  outcome: string
  amount: number | null
  authCode: string | null
  rrn: string | null
  maskedPan: string | null
  receiptText: string | null
  /** Платёж отменён: только по слову человека (`confirmVoid` после отмены на терминале). Автоотмены нет (решение владельца 18.10.2026). */
  voided: boolean
  /** Деньги списаны, чек доказанно не вышел: платёж остаётся как есть, дальше решает человек (повторить проведение, чек коррекции, отмена на терминале и `confirmVoid`). */
  needsManualVoid: boolean
}

/** Стадия плеча операции (оплата картой, чек): `none` — плеча нет, `intent` — намерение на диске, ответа нет, `replied` — устройство ответило. */
export type FiscalStage = 'none' | 'intent' | 'replied'

/** Наложение «отложена»: продажа с неразобранным исходом убрана с глаз, касса свободна; статус и деньги не тронуты. */
export interface FiscalParking {
  since: string | null
  /** `human` — отложил кассир; иначе кто отложил сам агент. */
  by: string
  /** Слова агента: `new-sale`, `timeout`, `restart`, `cashier`… */
  reason: string
  /** Предупреждение агента, например «исход чека неизвестен, возможен двойной чек при повторе». */
  warning: string | null
}

export interface FiscalResult {
  operationId: string
  status: FiscalStatus
  fiscal: FiscalDocument | null
  error: FiscalError | null
  /** Наличные в кассе после внесения или выплаты. */
  cashSum: number | null
  /** Касса сама открыла смену при первом документе. */
  shiftAutoOpened: boolean
  card: FiscalCard | null
  /** Операция отложена; `null` — не отложена. */
  parking: FiscalParking | null
  /** Предупреждение агента при успешной операции («пробит, но не напечатан»…): показывается дословно. */
  notice: string | null
  /** После повторной пробивки в накопителе больше одного документа: возможно, пробито два чека. Только пометка. */
  possibleDoubleReceipt: boolean
  /** Номер попытки чека (1 — исходная); `null` — агент не сказал. */
  attempt: number | null
  /** Стадии плеч, если агент их отдаёт. */
  stage: { card: FiscalStage | null; receipt: FiscalStage | null } | null
  /** Шаг ядра 1С — только в режиме рабочего места «1С»; в собственном ядре `null`. */
  oneCStage: string | null
  /** Агент прямо сказал, можно ли «Повторить проведение»; `null` — не сказал (собственное ядро решает проверками при вызове). */
  canRepunch: boolean | null
}

export type PaymentKind = 'cash' | 'electronically' | 'card'

export interface ReceiptItemDraft {
  name: string
  price: number
  quantity: number
  amount: number
  /** `vat22`, `vat20`, `none`… — как в CONTRACT.md. */
  vat: string
  /** Признак предмета расчёта (`commodity`, `service`…): агент требует его в каждой позиции. */
  paymentObject: string
  /** Признак способа расчёта (`full_payment`…): агент требует его в каждой позиции. */
  paymentMethod: string
  markingCode?: string | null
}

/**
 * Кассир в запросах чека, смены и наличных. `cashierId` — необязательный GUID узла `cashier` структуры ресурса Receipts (RP-OWN8):
 * агент кладёт его в `context.cashierId` выгрузки; без него сервер сохраняет чек как `pending-cashier`. Имя идентичностью не служит.
 */
export interface FiscalCashier {
  name: string
  inn?: string
  cashierId?: string
}

export interface ReceiptDraft {
  operationId: string
  type: 'sale' | 'refund'
  cashier: FiscalCashier
  items: readonly ReceiptItemDraft[]
  payments: readonly { type: PaymentKind; amount: number }[]
  customer?: { emailOrPhone: string }
  /** `print` (по умолчанию) или `electronic` — только с адресом покупателя. */
  print?: 'print' | 'electronic'
  taxation?: string
}

export type OperationKind = 'receipt' | 'shift' | 'cash'

export interface JournalEntry {
  operationId: string
  kind: OperationKind
  status: FiscalStatus
  at: string
  total: number | null
  /** 1 — приход, 2 — возврат прихода; для смены и наличных пусто. */
  receiptType: number | null
  fiscal: FiscalDocument | null
  error: FiscalError | null
  cardNeedsManualVoid: boolean
  /** Операция отложена (поле `parked` списка). */
  parked: boolean
}

export interface DeviceProblem {
  code: string
  /** `block` — продавать нельзя, `warn` — можно, но скажите. */
  severity: 'block' | 'warn'
  text: string
  action: string | null
}

export type DeviceConnection = 'ok' | 'no-device' | 'busy' | 'driver-missing' | 'error'

/** Единая картина «можно ли продавать» (`GET /v1/device`). */
export interface DeviceSnapshot {
  connection: DeviceConnection
  canSell: boolean
  problems: readonly DeviceProblem[]
  shift: { state: 'opened' | 'closed' | 'expired' | null; number: number | null; expiresAt: string | null; documentsCount: number | null } | null
  device: { paperPresent: boolean | null; coverOpened: boolean | null; blocked: boolean | null } | null
  ofd: { notSent: number | null; oldestNotSentAt: string | null; lastError: string | null } | null
}

export interface ReceiptTotals {
  count: number | null
  sum: number | null
  /** Суммы по формам оплаты (`cash`, `electronically`…). */
  payments: Readonly<Record<string, number>>
}

export interface CashMovement {
  count: number | null
  sum: number | null
}

/** Итоги смены для экрана «Закрыть смену»; чего касса не дала — `null`. */
export interface ShiftTotals {
  shiftNumber: number | null
  sales: ReceiptTotals | null
  returns: ReceiptTotals | null
  cashIn: CashMovement | null
  cashOut: CashMovement | null
  cashInDrawer: number | null
}

export interface PrintResult {
  status: 'done' | 'failed'
  error: FiscalError | null
}

export interface JournalFilter {
  status?: FiscalStatus
  kind?: OperationKind
  limit?: number
  /** `true` — только отложенные, `false` — только не отложенные. */
  parked?: boolean
}

/** Вид чека коррекции: по самостоятельному решению или по предписанию. */
export type CorrectionType = 'self' | 'instruction'

export interface CorrectionRequest {
  /** Номер последнего фискального документа, прочитанный человеком с кассы: доказывает, что он на неё посмотрел. */
  lastFiscalDocument: number
  correctionType?: CorrectionType
  /** Дата основания `yyyy.MM.dd`; нет — дата расчёта. */
  baseDate?: string
  /** Номер предписания (обязателен для `instruction`). */
  baseNumber?: string
}

/** Слово человека по оплате картой с неизвестным исходом. */
export interface CardResolution {
  /** `false` — деньги не списаны; `true` — списаны (нужен `rrn`). */
  charged: boolean
  rrn?: string
  authCode?: string
  /** Что человек проверил на терминале (слип, экран, банк). Агент требует поле в ОБОИХ исходах: пустое — `400`. */
  checked: string
  /** Почему закрывает человек (его слова). Агент требует поле в ОБОИХ исходах. */
  reason: string
}

/** Исход «Допечатать»: `still-blocked` — касса всё ещё не может печатать (бумага, крышка), `unknown` — проверьте ленту. */
export interface ContinuePrintResult {
  status: 'done' | 'still-blocked' | 'unknown'
  error: FiscalError | null
}

export interface FiscalAgentOptions {
  client: LocalAgentClient
  /** Порт выбранного агента; нет — агент не найден (`unreachable` без обращения к сети). */
  port: () => number | undefined
  /** Токен рабочего места. `renew: true` — прежний не подошёл (401), нужен новый; хранить токен вызывающий не обязан. */
  token: (renew: boolean) => Promise<string>
  /** Предел ожидания чека без карты, мс (по умолчанию 60 с: касса печатает). */
  receiptTimeoutMs?: number
  /** Предел ожидания чека с оплатой картой, мс (по умолчанию 200 с: терминал агента ждёт банк до 180 с). */
  cardTimeoutMs?: number
}

export interface FiscalAgent {
  /** Состояние кассы: можно ли продавать и что мешает. */
  device(): Promise<DeviceSnapshot>
  /** Продажа или возврат; карта в `payments` — агент сам проводит платёж и потом пробивает чек. */
  receipt(draft: ReceiptDraft): Promise<FiscalResult>
  /** Открыть или закрыть смену (закрытие — Z-отчёт). Смена закрывается только вручную. */
  shift(request: { operationId: string; type: 'open' | 'close'; cashier: FiscalCashier; print?: 'print' | 'electronic' }): Promise<FiscalResult>
  /** Внесение или выплата наличных. */
  cash(request: { operationId: string; type: 'in' | 'out'; amount: number; cashier: FiscalCashier; print?: 'print' | 'electronic' }): Promise<FiscalResult>
  /** Сохранённый итог операции; `null` — агент о такой не знает (команда до журнала не дошла). */
  operation(operationId: string): Promise<FiscalResult | null>
  /** Снять `in-doubt` по накопителю кассы: «чек пробит» или «чека нет, можно повторить с новым ключом». */
  recover(operationId: string): Promise<FiscalResult>
  /** «Отложить продажу»: слово кассира. Статус и деньги не трогаются, касса свободна. Повтор безвреден. Не `in-doubt` — `409 cannot-park`. */
  park(operationId: string): Promise<FiscalResult>
  /**
   * «Повторить проведение» одной кнопкой: без номера документа агент сам доказывает «чека нет» (журнал и два чтения счётчика).
   * С номером последнего документа — запасной путь. Деньги не трогаются, терминал не вызывается.
   */
  repunch(operationId: string, lastFiscalDocument?: number): Promise<FiscalResult>
  /** Чек коррекции вместо чека, который доказанно не пробит: настоящий фискальный документ, отменить нельзя. */
  correction(operationId: string, request: CorrectionRequest): Promise<FiscalResult>
  /** Слово человека по оплате картой с неизвестным исходом. */
  resolveCard(operationId: string, resolution: CardResolution): Promise<FiscalResult>
  /** Человек отменил платёж на терминале (или деньги вернулись): закрыть операцию. */
  confirmVoid(operationId: string): Promise<FiscalResult>
  /** «Допечатать» хвост пробитого чека, когда бумагу сменили; чек заново не пробивается. */
  continuePrint(): Promise<ContinuePrintResult>
  /** Журнал операций, новые первыми: после перезагрузки страницы здесь находят неразобранный `in-doubt`. */
  journal(filter?: JournalFilter): Promise<readonly JournalEntry[]>
  shiftTotals(): Promise<ShiftTotals>
  /** X-отчёт: смену не закрывает, фискального документа нет. */
  printReportX(cashier?: FiscalCashier): Promise<PrintResult>
  printLastReceiptCopy(): Promise<PrintResult>
}

/** Новый ключ операции (GUID). Создаётся до отправки и живёт вместе с корзиной: повтор — с тем же ключом. */
export function newOperationId(): string {
  return globalThis.crypto.randomUUID()
}

const operationPath = (operationId: string): string => `/v1/operations/${encodeURIComponent(operationId)}`

const DEFAULT_RECEIPT_TIMEOUT_MS = 60_000
/**
 * Чек с картой: терминал агента ждёт ответа банка до 180 с (docs агента), потом агент ещё пробивает чек. Клиент ждёт 200 с,
 * чтобы раньше агента не оборвать запрос: оборванный запрос с картой превращается в «не знаем» и в лишнюю сверку, хотя
 * агент ещё работает. Без карты — как было (60 с: касса печатает).
 */
export const DEFAULT_CARD_TIMEOUT_MS = 200_000
const COMMAND_TIMEOUT_MS = 60_000
const STATUSES: readonly FiscalStatus[] = ['done', 'failed', 'in-doubt']
const KINDS: readonly OperationKind[] = ['receipt', 'shift', 'cash']

export function createFiscalAgent(options: FiscalAgentOptions): FiscalAgent {
  const receiptTimeout = options.receiptTimeoutMs ?? DEFAULT_RECEIPT_TIMEOUT_MS
  const cardTimeout = options.cardTimeoutMs ?? DEFAULT_CARD_TIMEOUT_MS

  /** Запрос с токеном; 401 — команда не выполнялась, поэтому токен обновляется и запрос повторяется один раз. */
  async function call<T>(
    method: 'GET' | 'POST',
    path: string,
    decode: (raw: unknown) => T,
    spec: { body?: unknown; timeoutMs?: number } = {},
  ): Promise<T> {
    const port = options.port()
    if (port === undefined) throw new LocalAgentRequestError('unreachable', 'Кассовый агент на этом компьютере не найден.')
    const send = async (renew: boolean): Promise<T> =>
      options.client.request({ port, method, path, body: spec.body, token: await options.token(renew), decode, timeoutMs: spec.timeoutMs ?? COMMAND_TIMEOUT_MS })
    try {
      return await send(false)
    } catch (reason) {
      if (reason instanceof LocalAgentRequestError && reason.kind === 'unauthorized') return send(true)
      throw reason
    }
  }

  return {
    device: () => call('GET', '/v1/device', decodeDevice),
    receipt: (draft) =>
      call('POST', '/v1/operations', decodeResult, {
        body: draft,
        timeoutMs: draft.payments.some((payment) => payment.type === 'card') ? cardTimeout : receiptTimeout,
      }),
    shift: (request) => call('POST', '/v1/shift', decodeResult, { body: request, timeoutMs: receiptTimeout }),
    cash: (request) => call('POST', '/v1/cash', decodeResult, { body: request, timeoutMs: receiptTimeout }),
    async operation(operationId) {
      try {
        return await call('GET', `/v1/operations/${encodeURIComponent(operationId)}`, decodeResult)
      } catch (reason) {
        if (reason instanceof LocalAgentRequestError && reason.kind === 'refused' && reason.status === 404) return null
        throw reason
      }
    },
    recover: (operationId) => call('POST', `/v1/operations/${encodeURIComponent(operationId)}/recover`, decodeResult, { timeoutMs: receiptTimeout }),
    park: (operationId) => call('POST', `${operationPath(operationId)}/park`, decodeResult),
    repunch: (operationId, lastFiscalDocument) =>
      call('POST', `${operationPath(operationId)}/repunch`, decodeResult, {
        body: lastFiscalDocument === undefined ? { confirmed: true } : { confirmed: true, lastFiscalDocument },
        timeoutMs: receiptTimeout,
      }),
    correction: (operationId, request) =>
      call('POST', `${operationPath(operationId)}/correction`, decodeResult, { body: { confirmed: true, ...request }, timeoutMs: receiptTimeout }),
    resolveCard: (operationId, resolution) => call('POST', `${operationPath(operationId)}/resolve-card`, decodeResult, { body: resolution, timeoutMs: receiptTimeout }),
    confirmVoid: (operationId) => call('POST', `${operationPath(operationId)}/confirm-void`, decodeResult),
    continuePrint: () => call('POST', '/v1/receipts/continue-print', decodeContinuePrint, { timeoutMs: receiptTimeout }),
    async journal(filter = {}) {
      const query = new URLSearchParams()
      if (filter.status !== undefined) query.set('status', filter.status)
      if (filter.kind !== undefined) query.set('kind', filter.kind)
      if (filter.limit !== undefined) query.set('limit', String(filter.limit))
      if (filter.parked !== undefined) query.set('parked', String(filter.parked))
      const queryText = query.toString()
      return call('GET', `/v1/operations${queryText === '' ? '' : `?${queryText}`}`, decodeJournal)
    },
    shiftTotals: () => call('GET', '/v1/shift/totals', decodeShiftTotals),
    printReportX: (cashier) => call('POST', '/v1/reports/x', decodePrint, { body: cashier === undefined ? undefined : { cashier }, timeoutMs: receiptTimeout }),
    printLastReceiptCopy: () => call('POST', '/v1/receipts/last/copy', decodePrint, { timeoutMs: receiptTimeout }),
  }
}

// ---- разбор ответов на границе (D-056): лишним полям не удивляемся, отсутствие главных — понятная ошибка ----

const record = (raw: unknown, what: string): Record<string, unknown> => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) throw new Error(`${what}: ожидался объект`)
  return raw as Record<string, unknown>
}
const text = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

function decodeError(raw: unknown): FiscalError | null {
  if (raw === undefined || raw === null) return null
  const value = record(raw, 'ошибка агента')
  return { code: text(value.code) ?? 'unknown', text: text(value.text) ?? 'Ошибка без пояснения.', action: text(value.action) }
}

function decodeFiscal(raw: unknown): FiscalDocument | null {
  if (raw === undefined || raw === null) return null
  const value = record(raw, 'фискальный документ')
  return {
    shiftNumber: num(value.shiftNumber),
    receiptNumber: num(value.receiptNumber),
    fiscalDocumentNumber: num(value.fiscalDocumentNumber),
    fiscalSign: text(value.fiscalSign),
    fnNumber: text(value.fnNumber),
    dateTime: text(value.dateTime),
    total: num(value.total),
    registrationNumber: text(value.registrationNumber),
    printed: typeof value.printed === 'boolean' ? value.printed : null,
  }
}

const STAGES: readonly FiscalStage[] = ['none', 'intent', 'replied']
const stageOf = (value: unknown): FiscalStage | null => (typeof value === 'string' && (STAGES as readonly string[]).includes(value) ? (value as FiscalStage) : null)

function decodeStage(value: Record<string, unknown>): FiscalResult['stage'] {
  const raw = value.stage
  const nested = typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null
  const card = stageOf(nested?.card ?? value.cardStage)
  const receipt = stageOf(nested?.receipt ?? value.receiptStage)
  return card === null && receipt === null ? null : { card, receipt }
}

function decodeParking(raw: unknown): FiscalParking | null {
  if (raw === undefined || raw === null) return null
  const value = record(raw, 'наложение «отложена»')
  return { since: text(value.since), by: text(value.by) ?? 'unknown', reason: text(value.reason) ?? 'unknown', warning: text(value.warning) }
}

function decodeCard(raw: unknown): FiscalCard | null {
  if (raw === undefined || raw === null) return null
  const value = record(raw, 'оплата картой')
  return {
    outcome: text(value.outcome) ?? 'unknown',
    amount: num(value.amount),
    authCode: text(value.authCode),
    rrn: text(value.rrn),
    maskedPan: text(value.maskedPan),
    receiptText: text(value.receiptText),
    voided: value.voided === true,
    needsManualVoid: value.needsManualVoid === true,
  }
}

const status = (value: unknown): FiscalStatus => {
  if (typeof value === 'string' && (STATUSES as readonly string[]).includes(value)) return value as FiscalStatus
  throw new Error('ответ агента: неизвестный статус операции')
}

/** Ответ на чек, смену, наличные, `GET /v1/operations/{id}`, `recover`. Незнакомый статус — ошибка, а не «пробит». */
export function decodeResult(raw: unknown): FiscalResult {
  const value = record(raw, 'итог операции')
  return {
    operationId: text(value.operationId) ?? '',
    status: status(value.status),
    fiscal: decodeFiscal(value.fiscal),
    error: decodeError(value.error),
    cashSum: num(value.cashSum),
    shiftAutoOpened: value.shiftAutoOpened === true,
    card: decodeCard(value.card),
    parking: decodeParking(value.parking),
    notice: text(value.notice),
    possibleDoubleReceipt: value.possibleDoubleReceipt === true,
    // attempt/stage агент отдаёт лишь в записи журнала выгрузки; из маршрутов операции приходит null. Поля оставлены: на них опирается ход проведения (ownProgress).
    attempt: num(value.attempt),
    stage: decodeStage(value),
    oneCStage: text(value.oneCStage),
    canRepunch: typeof value.canRepunch === 'boolean' ? value.canRepunch : null,
  }
}

export function decodeJournal(raw: unknown): readonly JournalEntry[] {
  const items = record(raw, 'журнал операций').items
  if (!Array.isArray(items)) throw new Error('журнал операций: нет списка items')
  return items.map((item) => {
    const value = record(item, 'запись журнала')
    const kind = value.kind
    return {
      operationId: text(value.operationId) ?? '',
      kind: typeof kind === 'string' && (KINDS as readonly string[]).includes(kind) ? (kind as OperationKind) : 'receipt',
      status: status(value.status),
      at: text(value.at) ?? '',
      total: num(value.total),
      receiptType: num(value.receiptType),
      fiscal: decodeFiscal(value.fiscal),
      error: decodeError(value.error),
      cardNeedsManualVoid: value.cardNeedsManualVoid === true,
      parked: value.parked === true,
    }
  })
}

const CONNECTIONS: readonly DeviceConnection[] = ['ok', 'no-device', 'busy', 'driver-missing', 'error']

/**
 * Ответ `GET /v1/device`. Неизвестная связь читается как `error`, а `canSell` без явного `true` — «нельзя»: сомнение не должно
 * открывать продажу.
 */
export function decodeDevice(raw: unknown): DeviceSnapshot {
  const value = record(raw, 'состояние кассы')
  const connection = value.connection
  const problems = Array.isArray(value.problems) ? value.problems : []
  const shift = value.shift === undefined || value.shift === null ? null : record(value.shift, 'смена')
  const device = value.device === undefined || value.device === null ? null : record(value.device, 'устройство')
  const ofd = value.ofd === undefined || value.ofd === null ? null : record(value.ofd, 'ОФД')
  const flag = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)
  const shiftState = shift?.state
  return {
    connection: typeof connection === 'string' && (CONNECTIONS as readonly string[]).includes(connection) ? (connection as DeviceConnection) : 'error',
    canSell: value.canSell === true,
    problems: problems.map((item) => {
      const problem = record(item, 'неполадка')
      return {
        code: text(problem.code) ?? 'unknown',
        severity: problem.severity === 'warn' ? 'warn' : 'block',
        text: text(problem.text) ?? 'Неполадка без пояснения.',
        action: text(problem.action),
      }
    }),
    shift:
      shift === null
        ? null
        : {
            state: shiftState === 'opened' || shiftState === 'closed' || shiftState === 'expired' ? shiftState : null,
            number: num(shift.number),
            expiresAt: text(shift.expiresAt),
            documentsCount: num(shift.documentsCount),
          },
    device: device === null ? null : { paperPresent: flag(device.paperPresent), coverOpened: flag(device.coverOpened), blocked: flag(device.blocked) },
    ofd: ofd === null ? null : { notSent: num(ofd.notSent), oldestNotSentAt: text(ofd.oldestNotSentAt), lastError: text(ofd.lastError) },
  }
}

function decodeReceiptTotals(raw: unknown): ReceiptTotals | null {
  if (raw === undefined || raw === null) return null
  const value = record(raw, 'итоги чеков')
  const payments: Record<string, number> = {}
  if (typeof value.payments === 'object' && value.payments !== null) {
    for (const [name, sum] of Object.entries(value.payments as Record<string, unknown>)) {
      const amount = num(sum)
      if (amount !== null) payments[name] = amount
    }
  }
  return { count: num(value.count), sum: num(value.sum), payments }
}

function decodeMovement(raw: unknown): CashMovement | null {
  if (raw === undefined || raw === null) return null
  const value = record(raw, 'движение наличных')
  return { count: num(value.count), sum: num(value.sum) }
}

export function decodeShiftTotals(raw: unknown): ShiftTotals {
  const value = record(raw, 'итоги смены')
  return {
    shiftNumber: num(value.shiftNumber),
    sales: decodeReceiptTotals(value.sales),
    returns: decodeReceiptTotals(value.returns),
    cashIn: decodeMovement(value.cashIn),
    cashOut: decodeMovement(value.cashOut),
    cashInDrawer: num(value.cashInDrawer),
  }
}

function decodePrint(raw: unknown): PrintResult {
  const value = record(raw, 'результат печати')
  if (value.status !== 'done' && value.status !== 'failed') throw new Error('результат печати: неизвестный статус')
  return { status: value.status, error: decodeError(value.error) }
}

const CONTINUE_PRINT: readonly ContinuePrintResult['status'][] = ['done', 'still-blocked', 'unknown']

/** Ответ «Допечатать». Незнакомый статус — ошибка, а не «допечатано». */
export function decodeContinuePrint(raw: unknown): ContinuePrintResult {
  const value = record(raw, 'результат допечатки')
  const status = value.status
  if (typeof status !== 'string' || !(CONTINUE_PRINT as readonly string[]).includes(status)) throw new Error('результат допечатки: неизвестный статус')
  return { status: status as ContinuePrintResult['status'], error: decodeError(value.error) }
}
